// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.

import { normalizeGroup, normalizeGroupsWithHierarchy } from './equipmentUtils';

const isCurrentSelectedEquipment = (state, coEquipUuid) =>
  state?.selectedEquipment?.co_equip_uuid === coEquipUuid;

const isCurrentSelectedEquipmentGroup = (state, groupUuid) =>
  state?.selectedEquipmentGroup?.equip_group_uuid === groupUuid;

const isCurrentCameraControlGroup = (state, groupUuid) =>
  state?.cameraControlGroup?.equip_group_uuid === groupUuid;

const isCurrentSelectedPresetSequence = (state, presetSequenceUuid) =>
  state?.selectedPresetSequence?.preset_seq_uuid === presetSequenceUuid;

export const fetchCompanyEquipmentGroupsState = (state, groups) => {
  state.equipmentGroups = normalizeGroupsWithHierarchy(groups);
};
export const createEquipmentGroupState = (state, newGroup) => {
  const currentGroups = state.equipmentGroups ?? [];

  const updatedGroups = normalizeGroupsWithHierarchy([...currentGroups, normalizeGroup(newGroup)]);

  state.equipmentGroups = updatedGroups;
};

export const updateEquipmentGroupState = (state, updatedGroup, companyEquipment) => {
  const updatedGroups = state.equipmentGroups.map(g =>
    g.equip_group_uuid === updatedGroup.equip_group_uuid
      ? { ...g, ...normalizeGroup(updatedGroup, companyEquipment) }
      : g
  );

  state.equipmentGroups = normalizeGroupsWithHierarchy(updatedGroups, companyEquipment);

  const normalizedGroup = state.equipmentGroups.find(
    g => g.equip_group_uuid === updatedGroup.equip_group_uuid
  );

  if (isCurrentSelectedEquipmentGroup(state, normalizedGroup?.equip_group_uuid)) {
    state.selectedEquipmentGroup = {
      ...state.selectedEquipmentGroup,
      ...normalizedGroup,
    };
  }

  if (isCurrentCameraControlGroup(state, normalizedGroup?.equip_group_uuid)) {
    state.cameraControlGroup = {
      ...state.cameraControlGroup,
      ...normalizedGroup,
    };
  }
};

export const deleteEquipmentGroupState = (state, group) => {
  const { equip_group_uuid: groupUuid } = group;

  state.equipmentGroups = state.equipmentGroups.filter(g => g.equip_group_uuid !== groupUuid);

  if (isCurrentSelectedEquipmentGroup(state, groupUuid)) {
    state.selectedEquipmentGroup = null;
  }

  if (isCurrentCameraControlGroup(state, groupUuid)) {
    state.cameraControlGroup = null;
  }
};

export const selectEquipmentGroupState = (state, group) => {
  const normalizedGroup = normalizeGroup(group);
  state.selectedEquipmentGroup = normalizedGroup;
};

export const selectCameraControlGroupState = (state, group) => {
  const normalizedGroup = normalizeGroup(group);
  state.cameraControlGroup = normalizedGroup;
};

// Update company equipment inside equipment group
export const updateCompanyEquipmentInGroupState = (state, updatedEquipment) => {
  if (isCurrentSelectedEquipment(state, updatedEquipment.co_equip_uuid)) {
    state.selectedEquipment = {
      ...state.selectedEquipment,
      ...updatedEquipment,
    };
  }
  // update equipment in equipmentGroups state to show in nodes
  state.equipmentGroups = state.equipmentGroups.map(group => {
    if (group.equipment?.find(e => e.co_equip_uuid === updatedEquipment.co_equip_uuid)) {
      return {
        ...group,
        equipment: group.equipment.map(e =>
          e.co_equip_uuid === updatedEquipment.co_equip_uuid ? { ...e, ...updatedEquipment } : e
        ),
      };
    }
    return group;
  });
};

// Group Equipment lock state update helpers
export const addLockOnEquipmentGroupState = (state, action) => {
  const addGroupLock = group =>
    group.equip_group_uuid === action.equip_group_uuid ? { ...group, lock: [action] } : group;

  state.equipmentGroups = state.equipmentGroups?.map(g => addGroupLock(g));

  if (state.selectedEquipmentGroup) {
    state.selectedEquipmentGroup = addGroupLock(state.selectedEquipmentGroup);
  }

  if (state.cameraControlGroup) {
    state.cameraControlGroup = addGroupLock(state.cameraControlGroup);
  }
};

export const updateLockOnEquipmentGroupState = (state, action) => {
  const updateGroupLock = group =>
    group.equip_group_uuid === action.equip_group_uuid &&
    group.lock.length > 0 &&
    group.lock[0]?.lock_uuid === action.lock_uuid
      ? { ...group, lock: [action] }
      : group;

  state.equipmentGroups = state.equipmentGroups.map(updateGroupLock);

  if (state.selectedEquipmentGroup) {
    state.selectedEquipmentGroup = updateGroupLock(state.selectedEquipmentGroup);
  }

  if (state.cameraControlGroup) {
    state.cameraControlGroup = updateGroupLock(state.cameraControlGroup);
  }
};

export const deleteLockOnEquipmentGroupState = (state, action) => {
  const deleteGroupLock = group =>
    group.equip_group_uuid === action.equip_group_uuid &&
    group.lock.length > 0 &&
    group.lock[0]?.lock_uuid === action.lock_uuid
      ? { ...group, lock: [] }
      : group;

  state.equipmentGroups = state.equipmentGroups.map(deleteGroupLock);

  if (state.selectedEquipmentGroup) {
    state.selectedEquipmentGroup = deleteGroupLock(state.selectedEquipmentGroup);
  }

  if (state.cameraControlGroup) {
    state.cameraControlGroup = deleteGroupLock(state.cameraControlGroup);
  }
};

// Preset state update helpers
export const createPresetState = (state, newPreset) => {
  if (isCurrentSelectedEquipmentGroup(state, newPreset.equip_group_uuid)) {
    state.presets = [...state.presets, newPreset];
  }
};

export const updatePresetState = (state, updatedPreset) => {
  const exists = state.presets.some(p => p.preset_uuid === updatedPreset.preset_uuid);
  if (!exists) {
    return;
  }
  state.presets = state.presets.map(preset =>
    preset.preset_uuid === updatedPreset.preset_uuid ? { ...preset, ...updatedPreset } : preset
  );
};

export const deletePresetState = (state, presetUuid) => {
  state.presets = state.presets.filter(preset => preset.preset_uuid !== presetUuid);
};

const applyPresetState = (state, action) => {
  const { presetUUID, sequenceUUID } = action || {};
  const selectedGroup = state?.cameraControlGroup;
  if (presetUUID) {
    const presetToApply = state.presets.find(preset => preset.preset_uuid === presetUUID);
    // preset don t belong to the selected camera control group, ignore the message
    if (presetToApply?.equip_group_uuid !== selectedGroup?.equip_group_uuid) {
      return;
    }
    // add preset object to cameraControlGroup
    state.cameraControlGroup = {
      ...state.cameraControlGroup,
      preset: {
        type: 'preset',
        preset: presetToApply,
        ...action,
      },
    };
  } else if (sequenceUUID) {
    const sequenceToApply = state.presetSequences.find(seq => seq.preset_seq_uuid === sequenceUUID);
    // sequence don t belong to the selected camera control group, ignore the message
    if (sequenceToApply?.equip_group_uuid !== selectedGroup?.equip_group_uuid) {
      return;
    }
    state.cameraControlGroup = {
      ...state.cameraControlGroup,
      preset: {
        type: 'sequence',
        sequence: sequenceToApply,
        ...action,
      },
    };
  }
};

const stopPresetState = (state, action) => {
  const selectedGroup = state?.cameraControlGroup;
  // stop preset if the presetUUID in the message is the same as the one in the cameraControlGroup
  if (
    selectedGroup?.preset?.preset?.preset_uuid === action?.presetUUID &&
    selectedGroup?.preset?.sequence?.preset_seq_uuid === action?.sequenceUUID
  ) {
    state.cameraControlGroup = {
      ...state.cameraControlGroup,
      preset: null,
    };
  }
};

// This should be a temporary solution until the websocket is send as events message instead of sending the json message in equipment settings.This reducer will create performance issues due to many EquipmentInStudioDesiredStateFromApi event messages coming for equipment settings or properties changes.
export const updatePresetManagerState = (state, action) => {
  const { settings } = action || {};
  if (!settings?.action_message) {
    return;
  }

  let parsedMessage;

  try {
    parsedMessage = JSON.parse(settings.action_message);
  } catch (e) {
    state.error.presetSequences = 'Invalid preset manager action message received';
  }
  const { event } = parsedMessage || {};
  if (!event) {
    return;
  }

  switch (event) {
    case 'StudioEquipmentApplyPreset':
      applyPresetState(state, parsedMessage);
      break;
    case 'StudioEquipmentStopPreset':
      stopPresetState(state, parsedMessage);
      break;
    case 'StudioEquipmentApplyPresetSequence':
      applyPresetState(state, parsedMessage);
      break;
    case 'StudioEquipmentStopPresetSequence':
      stopPresetState(state, parsedMessage);
      break;
    default:
      break;
  }
};

// remove preset from Camera Control Group
export const clearPresetState = (state, action) => {
  const { equip_group_uuid: groupUuid } = action || {};
  if (isCurrentSelectedEquipmentGroup(state, groupUuid)) {
    state.cameraControlGroup = {
      ...state.cameraControlGroup,
      preset: null,
    };
  }
};

// Preset Sequence state update helpers
export const createPresetSequenceState = (state, newPresetSequence) => {
  if (isCurrentSelectedEquipmentGroup(state, newPresetSequence.equip_group_uuid)) {
    state.presetSequences = [...state.presetSequences, newPresetSequence];
  }
};

export const updatePresetSequenceState = (state, updatedPresetSequence) => {
  if (isCurrentSelectedEquipmentGroup(state, updatedPresetSequence.equip_group_uuid)) {
    state.presetSequences = state.presetSequences.map(presetSequence =>
      presetSequence.preset_seq_uuid === updatedPresetSequence.preset_seq_uuid
        ? { ...presetSequence, ...updatedPresetSequence }
        : presetSequence
    );
  }
  if (isCurrentSelectedPresetSequence(state, updatedPresetSequence.preset_seq_uuid)) {
    state.selectedPresetSequence = {
      ...state.selectedPresetSequence,
      ...updatedPresetSequence,
    };
  }
};

export const deletePresetSequenceState = (state, presetSequenceUuid) => {
  state.presetSequences = state.presetSequences.filter(
    presetSequence => presetSequence.preset_seq_uuid !== presetSequenceUuid
  );
  if (isCurrentSelectedPresetSequence(state, presetSequenceUuid)) {
    state.selectedPresetSequence = null;
  }
};

export const createPresetSequenceStepState = (state, newStep) => {
  const { preset_seq_uuid: presetSequenceUuid } = newStep;
  const sequence = state.presetSequences.find(seq => seq.preset_seq_uuid === presetSequenceUuid);
  if (!sequence) {
    return;
  }
  if (sequence) {
    sequence.preset_sequence_step = [...(sequence.preset_sequence_step || []), newStep];
  }
  if (isCurrentSelectedPresetSequence(state, presetSequenceUuid)) {
    state.selectedPresetSequence.preset_sequence_step = [
      ...(state.selectedPresetSequence.preset_sequence_step || []),
      newStep,
    ];
  }
};

export const updatePresetSequenceStepState = (state, updatedStep) => {
  const { preset_seq_step_uuid: stepUuid, preset_seq_uuid: sequenceUuid } = updatedStep;
  const sequence = state.presetSequences.find(seq => seq.preset_seq_uuid === sequenceUuid);
  if (!sequence) {
    return;
  }
  if (sequence?.preset_sequence_step) {
    sequence.preset_sequence_step = sequence.preset_sequence_step.map(step =>
      step.preset_seq_step_uuid === stepUuid ? { ...step, ...updatedStep } : step
    );
  }
  if (isCurrentSelectedPresetSequence(state, sequenceUuid)) {
    state.selectedPresetSequence.preset_sequence_step =
      state.selectedPresetSequence.preset_sequence_step?.map(step =>
        step.preset_seq_step_uuid === stepUuid ? { ...step, ...updatedStep } : step
      ) || [];
  }
};

export const deletePresetSequenceStepState = (state, step) => {
  const { preset_seq_step_uuid: stepUuid, preset_seq_uuid: sequenceUuid } = step;
  const sequence = state.presetSequences.find(seq => seq.preset_seq_uuid === sequenceUuid);
  if (!sequence) {
    return;
  }
  if (sequence?.preset_sequence_step) {
    sequence.preset_sequence_step = sequence.preset_sequence_step.filter(
      step => step.preset_seq_step_uuid !== stepUuid
    );
  }
  if (isCurrentSelectedPresetSequence(state, sequenceUuid)) {
    state.selectedPresetSequence.preset_sequence_step =
      state.selectedPresetSequence.preset_sequence_step?.filter(
        step => step.preset_seq_step_uuid !== stepUuid
      ) || [];
  }
};
